import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import App from "./App";
import { queryClient } from "@/lib/queryClient";
import { AuthProvider } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { captureKeep } from "@/lib/studio/keep";
import "./index.css";

/**
 * The app has one canonical origin: app.itsbloxx.com.
 *
 * Cloudflare Pages always serves the project's *.pages.dev subdomain and there
 * is no way to switch it off, so without this the app answers on two origins.
 * That is not just untidy. The Google OAuth redirect URI is derived from
 * `window.location.origin` (see routes/Integrations.tsx), and only
 * app.itsbloxx.com is registered on the OAuth client, so a Google connection
 * started from pages.dev fails with redirect_uri_mismatch. Google will not
 * accept pages.dev as an authorized domain either: it is on the public suffix
 * list.
 *
 * This is deliberately NOT in public/_redirects. Pages matches those rules on
 * PATH only, so an absolute-URL source is silently ignored (verified: it
 * deployed and did nothing). A zone-level redirect rule is the other correct
 * option, but pages.dev is not a zone in the account, so it is not available.
 *
 * Runs before render so nothing authenticates against the wrong origin first.
 */
const CANONICAL_HOST = "app.itsbloxx.com";
if (window.location.hostname.endsWith(".pages.dev")) {
  window.location.replace(
    `https://${CANONICAL_HOST}${window.location.pathname}${window.location.search}${window.location.hash}`
  );
}

/**
 * A recording made on bloxx.video without an account arrives as
 * `#keep=<id>.<token>`. Read it before the router runs: RequireAuth sends a
 * signed-out visitor to /login with the pathname alone, and the fragment would
 * be gone by the time Studio mounts. See lib/studio/keep.ts.
 */
captureKeep();

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>
);
